import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import { useAuth } from '../context/AuthContext'
import styles from './AnalyticsPage.module.css'

interface Campaign {
  id: number
  product: string
  platform: string
  budget: string
  performance_score: number
  headline?: string
  status?: string
}

interface MetaInsight {
  campaign_id: string
  campaign_name: string
  impressions?: string
  clicks?: string
  spend?: string
  ctr?: string
  cpc?: string
  reach?: string
}

// ─── Inline SVG Bar Chart ─────────────────────────────────────────────────────

function BarChartSvg({ data }: { data: { name: string; value: number; color: string }[] }) {
  if (!data.length) return null
  const W = 600, H = 180, padL = 40, padR = 16, padT = 16, padB = 40
  const innerW = W - padL - padR
  const innerH = H - padT - padB
  const maxVal = Math.max(...data.map(d => d.value), 1)
  const barW = Math.min(48, (innerW / data.length) - 8)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(p => {
        const y = padT + innerH * (1 - p)
        return (
          <g key={p}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e5e9f0" strokeWidth="1" />
            <text x={padL - 6} y={y + 4} fontSize="10" fill="#6b7280" textAnchor="end" fontFamily="inherit">
              {Math.round(maxVal * p)}
            </text>
          </g>
        )
      })}
      {data.map((d, i) => {
        const x = padL + (i / data.length) * innerW + (innerW / data.length - barW) / 2
        const barH = (d.value / maxVal) * innerH
        const y = padT + innerH - barH
        const label = d.name.length > 10 ? d.name.slice(0, 10) + '…' : d.name
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={barW} height={barH} rx="4" fill={d.color} opacity="0.85" />
            <text x={x + barW / 2} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="middle" fontFamily="inherit">
              {label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const { isPro, plan, planExpiresAt } = useAuth()
  const navigate = useNavigate()

  const planExpired = planExpiresAt ? new Date(planExpiresAt) < new Date() : false
  const hasAccess = isPro && !planExpired

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [metaInsights, setMetaInsights] = useState<MetaInsight[]>([])
  const [refreshingMeta, setRefreshingMeta] = useState(false)
  const [sortKey, setSortKey] = useState<keyof Campaign>('performance_score')
  const [sortAsc, setSortAsc] = useState(false)

  useEffect(() => {
    if (!hasAccess) return
    fetchCampaigns()
    fetchMetaInsights()
    const interval = window.setInterval(fetchMetaInsights, 60000)
    return () => window.clearInterval(interval)
  }, [hasAccess])

  async function fetchCampaigns() {
    try {
      const res = await apiClient.get('/campaigns')
      setCampaigns(res.data)
    } catch (e) { console.error(e) }
  }

  async function fetchMetaInsights() {
    try {
      setRefreshingMeta(true)
      const res = await apiClient.get('/facebook/insights')
      // Backend returns a single object {impressions, clicks, spend, ctr, cpc, reach, roas, conversions}
      // not an array — normalize into display format
      const d = res.data
      if (d && d.impressions !== undefined) {
        setMetaInsights([{
          campaign_id: 'account',
          campaign_name: 'Account Total (Last 30 days)',
          impressions: String(d.impressions || 0),
          clicks: String(d.clicks || 0),
          spend: String(Number(d.spend || 0).toFixed(2)),
          ctr: String(Number(d.ctr || 0).toFixed(4)),
          cpc: String(Number(d.cpc || 0).toFixed(2)),
          reach: String(d.reach || 0),
        }])
      } else {
        setMetaInsights([])
      }
    } catch (e) { console.error(e); setMetaInsights([]) }
    finally { setRefreshingMeta(false) }
  }

  const totalCampaigns = campaigns.length
  const avgScore = campaigns.length
    ? (campaigns.reduce((s, c) => s + c.performance_score, 0) / campaigns.length).toFixed(1)
    : '—'
  const totalBudget = campaigns.reduce((s, c) => {
    const m = c.budget?.match(/\d+/)
    return s + (m ? parseInt(m[0]) : 0)
  }, 0)
  const topScore = campaigns.length ? Math.max(...campaigns.map(c => c.performance_score)) : 0

  const kpis = [
    { label: 'Total Campaigns', value: String(totalCampaigns), icon: '📁', color: '#1d4ed8' },
    { label: 'Avg AI Score', value: String(avgScore), icon: '🤖', color: '#7c3aed' },
    { label: 'Total Budget', value: `₹${totalBudget.toLocaleString()}`, icon: '💰', color: '#059669' },
    { label: 'Top Score', value: String(topScore), icon: '🏆', color: '#d97706' },
  ]

  const sorted = [...campaigns].sort((a, b) => {
    const av = a[sortKey] as number | string
    const bv = b[sortKey] as number | string
    return sortAsc
      ? av > bv ? 1 : -1
      : av < bv ? 1 : -1
  })

  const chartData = campaigns
    .sort((a, b) => b.performance_score - a.performance_score)
    .slice(0, 8)
    .map((c, i) => ({
      name: c.product,
      value: c.performance_score,
      color: ['#1d4ed8','#6366f1','#7c3aed','#0891b2','#059669','#d97706','#dc2626','#db2777'][i % 8],
    }))

  const handleSort = (key: keyof Campaign) => {
    if (sortKey === key) setSortAsc(!sortAsc)
    else { setSortKey(key); setSortAsc(false) }
  }

  const sortIcon = (key: keyof Campaign) =>
    sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : ''

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.heading}>Analytics</h1>
          <p className={styles.subheading}>Campaign performance overview and insights</p>
        </div>
      </div>

      {!hasAccess && (
        <div style={{
          background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px',
          padding: '2rem', textAlign: 'center', marginBottom: '1.5rem'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
          <h2 style={{ margin: '0 0 0.5rem', color: '#92400e' }}>Pro Feature</h2>
          <p style={{ color: '#78350f', margin: '0 0 1rem' }}>
            {planExpired
              ? `Your ${plan} plan expired on ${new Date(planExpiresAt!).toLocaleDateString()}. Contact your admin to renew.`
              : 'Analytics is available on the Pro plan. Contact your admin to upgrade.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#f59e0b', color: '#fff', border: 'none',
              borderRadius: '6px', padding: '0.5rem 1.5rem', cursor: 'pointer', fontWeight: 600
            }}
          >
            Back to Dashboard
          </button>
        </div>
      )}

      {hasAccess && <>

      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        {kpis.map(k => (
          <div key={k.label} className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: k.color + '18', color: k.color }}>
              {k.icon}
            </div>
            <div>
              <div className={styles.kpiValue}>{k.value}</div>
              <div className={styles.kpiLabel}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Campaign Performance Scores</h2>
          <span className={styles.cardSubtitle}>Top 8 campaigns by AI score</span>
        </div>
        <div className={styles.cardBody}>
          {chartData.length === 0
            ? <p className={styles.empty}>No campaigns yet.</p>
            : <BarChartSvg data={chartData} />
          }
        </div>
      </div>

      {/* Campaigns Table */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>All Campaigns</h2>
          <span className={styles.cardSubtitle}>{totalCampaigns} total · click headers to sort</span>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {(['product','platform','budget','performance_score'] as (keyof Campaign)[]).map(col => (
                  <th key={col} className={styles.th} onClick={() => handleSort(col)} style={{ cursor: 'pointer' }}>
                    {col.replace(/_/g, ' ')}{sortIcon(col)}
                  </th>
                ))}
                <th className={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => (
                <tr key={c.id} className={styles.tr}>
                  <td className={styles.tdBold}>{c.product}</td>
                  <td className={styles.td}>{c.platform || '—'}</td>
                  <td className={styles.td}>{c.budget || '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.scoreBar}>
                      <div
                        className={styles.scoreBarFill}
                        style={{
                          width: `${c.performance_score}%`,
                          background: c.performance_score >= 80 ? '#059669' : c.performance_score >= 60 ? '#d97706' : '#dc2626'
                        }}
                      />
                      <span className={styles.scoreNum}>{c.performance_score}</span>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <span className={`${styles.badge} ${c.status === 'active' ? styles.badgeActive : styles.badgeDraft}`}>
                      {c.status || 'draft'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live Meta Insights */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Live Meta Insights</h2>
          <button className={styles.refreshBtn} onClick={fetchMetaInsights} disabled={refreshingMeta}>
            {refreshingMeta ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {['Campaign','Impressions','Clicks','Spend','CTR','CPC','Reach'].map(h => (
                  <th key={h} className={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metaInsights.length === 0 ? (
                <tr><td colSpan={7} className={styles.td} style={{ textAlign: 'center', padding: '2rem' }}>
                  No Meta insights loaded yet. Connect your Facebook account in Settings.
                </td></tr>
              ) : metaInsights.map(row => (
                <tr key={row.campaign_id} className={styles.tr}>
                  <td className={styles.tdBold}>{row.campaign_name}</td>
                  <td className={styles.td}>{Number(row.impressions || 0).toLocaleString()}</td>
                  <td className={styles.td}>{Number(row.clicks || 0).toLocaleString()}</td>
                  <td className={styles.td}>₹{Number(row.spend || 0).toFixed(2)}</td>
                  <td className={styles.td}>{row.ctr || '0'}%</td>
                  <td className={styles.td}>₹{row.cpc || '0'}</td>
                  <td className={styles.td}>{Number(row.reach || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      }
    </div>
  )
}
